(() => {
  const ready = (fn) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  let pendingFrame = null;
  const scrollHandlers = new Set();
  const onScroll = () => {
    if (pendingFrame) return;
    pendingFrame = requestAnimationFrame(() => {
      pendingFrame = null;
      scrollHandlers.forEach((fn) => fn());
    });
  };
  const registerScroll = (fn) => {
    if (scrollHandlers.size === 0) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }
    scrollHandlers.add(fn);
    fn();
  };

  let revealObserver = null;
  const observeReveal = (root) => {
    if (!('IntersectionObserver' in window)) {
      root.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
      );
    }
    root.querySelectorAll('[data-reveal]').forEach((el) => revealObserver.observe(el));
  };

  const setupMockup = (section) => {
    if (section.dataset.nurjiInit === '1') return;
    section.dataset.nurjiInit = '1';

    const header = section.querySelector('[data-sticky-header]');
    const heroMedia = section.querySelector('[data-hero-media]');
    const navLinks = section.querySelectorAll('.nurji-nav a[href^="#"]');
    const productCards = section.querySelectorAll('[data-tilt]');

    if (header) {
      registerScroll(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 16);
      });
    }

    if (heroMedia) {
      registerScroll(() => {
        const rect = heroMedia.getBoundingClientRect();
        const viewport = window.innerHeight || 1;
        const offset = rect.top + rect.height * 0.5 - viewport * 0.5;
        const clamped = Math.max(-1, Math.min(1, offset / viewport));
        heroMedia.style.setProperty('--hero-shift', `${(clamped * -10).toFixed(2)}px`);
      });
    }

    navLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const targetSelector = link.getAttribute('href');
        if (!targetSelector || targetSelector === '#') return;
        const target = section.querySelector(targetSelector);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    observeReveal(section);

    productCards.forEach((card) => {
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.setProperty('--tilt-x', `${(-y * 5).toFixed(2)}deg`);
        card.style.setProperty('--tilt-y', `${(x * 5).toFixed(2)}deg`);
      });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      });
    });
  };

  const setupHamburger = () => {
    // Initial state: ensure all toggles report closed.
    document.querySelectorAll('[data-menu-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
      const navId = toggle.getAttribute('aria-controls');
      const nav = navId ? document.getElementById(navId) : document.querySelector('[data-nav-menu]');
      if (nav) nav.classList.remove('is-open');
    });

    // Event delegation on document so clicks always work, even if
    // the button is re-rendered or a sibling listener throws elsewhere.
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest && event.target.closest('[data-menu-toggle]');
      if (!toggle) return;
      event.preventDefault();
      const navId = toggle.getAttribute('aria-controls');
      const nav = navId ? document.getElementById(navId) : document.querySelector('[data-nav-menu]');
      if (!nav) return;
      const willOpen = !nav.classList.contains('is-open');
      nav.classList.toggle('is-open', willOpen);
      toggle.setAttribute('aria-expanded', String(willOpen));
      if (willOpen) {
        const firstLink = nav.querySelector('a, button');
        if (firstLink && firstLink.focus) firstLink.focus({ preventScroll: true });
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-nav-menu].is-open').forEach((nav) => {
        nav.classList.remove('is-open');
        const navId = nav.id;
        const toggle = navId
          ? document.querySelector('[data-menu-toggle][aria-controls="' + navId + '"]')
          : document.querySelector('[data-menu-toggle]');
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
          if (toggle.focus) toggle.focus({ preventScroll: true });
        }
      });
    });
  };

  const setupSubmenuToggles = () => {
    const isMobile = () => window.matchMedia('(max-width: 800px)').matches;
    document.querySelectorAll('[data-submenu-toggle]').forEach((toggle) => {
      const group = toggle.closest('[data-submenu]');
      if (!group) return;

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const willOpen = !group.classList.contains('is-open');
        // Close siblings on mobile to avoid stacking
        if (isMobile() && willOpen) {
          document.querySelectorAll('[data-submenu].is-open').forEach((g) => {
            if (g !== group) {
              g.classList.remove('is-open');
              const t = g.querySelector('[data-submenu-toggle]');
              if (t) t.setAttribute('aria-expanded', 'false');
            }
          });
        }
        group.classList.toggle('is-open', willOpen);
        toggle.setAttribute('aria-expanded', String(willOpen));
      });
    });
  };

  const setupQuickAdd = () => {
    document.querySelectorAll('.nurji-quick-add-form').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const submitBtn = form.querySelector('.nurji-quick-add-btn');
        const btnText = submitBtn?.querySelector('.btn-text');
        if (!submitBtn || !btnText) return;
        const originalText = btnText.textContent;

        submitBtn.classList.add('is-loading');
        submitBtn.disabled = true;
        btnText.textContent = 'Wird hinzugefügt…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new FormData(form),
        })
          .then((response) => response.json())
          .then(() => {
            submitBtn.classList.remove('is-loading');
            submitBtn.classList.add('is-added');
            btnText.textContent = 'Hinzugefügt!';
            document.dispatchEvent(new CustomEvent('cart:build'));
            setTimeout(() => {
              submitBtn.classList.remove('is-added');
              submitBtn.disabled = false;
              btnText.textContent = originalText;
            }, 3000);
          })
          .catch((error) => {
            console.error('Cart Error:', error);
            submitBtn.classList.remove('is-loading');
            submitBtn.disabled = false;
            btnText.textContent = 'Fehler!';
            setTimeout(() => {
              btnText.textContent = originalText;
            }, 2000);
          });
      });
    });
  };

  const setupSlideshow = () => {
    document.querySelectorAll('[data-slideshow]').forEach((slideshow) => {
      const slides = slideshow.querySelectorAll('.slide');
      const dots = slideshow.querySelectorAll('.dot');
      const nextBtn = slideshow.querySelector('.next');
      const prevBtn = slideshow.querySelector('.prev');
      const total = slides.length;
      if (total <= 1) return;

      let currentIndex = 0;
      let autoPlayInterval = null;

      const goToSlide = (index) => {
        slides[currentIndex].classList.remove('is-active');
        if (dots.length) dots[currentIndex].classList.remove('active');
        currentIndex = index;
        slides[currentIndex].classList.add('is-active');
        if (dots.length) dots[currentIndex].classList.add('active');
      };

      const nextSlide = () => goToSlide((currentIndex + 1) % total);
      const prevSlide = () => goToSlide((currentIndex - 1 + total) % total);

      const startInterval = () => {
        if (autoPlayInterval || document.hidden) return;
        autoPlayInterval = setInterval(nextSlide, 7000);
      };
      const stopInterval = () => {
        if (!autoPlayInterval) return;
        clearInterval(autoPlayInterval);
        autoPlayInterval = null;
      };
      const resetInterval = () => {
        stopInterval();
        startInterval();
      };

      nextBtn?.addEventListener('click', () => { nextSlide(); resetInterval(); });
      prevBtn?.addEventListener('click', () => { prevSlide(); resetInterval(); });
      dots.forEach((dot, i) => dot.addEventListener('click', () => { goToSlide(i); resetInterval(); }));

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stopInterval(); else startInterval();
      });

      startInterval();
    });
  };

  // Katalog-Cards: Quick-Add raus aus card-gallery + Preis und Button in einen
  // gemeinsamen flex-row Wrapper, damit beide auf gleicher Höhe sitzen.
  const moveCatalogQuickAdds = (root = document) => {
    const cards = root.querySelectorAll('div.product-grid__card');
    cards.forEach((card) => {
      if (card.dataset.qaMoved === '1') return;

      const qa = card.querySelector('.quick-add');
      const price = card.querySelector('product-price');

      // 1. Quick-Add aus card-gallery raus an den Card-Boden
      if (qa && qa.parentElement !== card) {
        card.appendChild(qa);
      }

      // 2. Preis + Quick-Add in einen Flex-Row-Wrapper packen
      if (price && qa && !card.querySelector('.nurji-pricerow')) {
        const wrap = document.createElement('div');
        wrap.className = 'nurji-pricerow';
        price.parentElement.insertBefore(wrap, price);
        wrap.appendChild(price);
        wrap.appendChild(qa);
      }

      if (qa) card.dataset.qaMoved = '1';
    });
  };

  const safe = (label, fn) => {
    try { fn(); } catch (err) { console.warn('[nurji]', label, err); }
  };

  ready(() => {
    safe('hamburger', setupHamburger);
    safe('submenuToggles', setupSubmenuToggles);
    document.querySelectorAll('.nurji-mockup').forEach((section) => {
      safe('mockup', () => setupMockup(section));
    });
    safe('quickAdd', setupQuickAdd);
    safe('slideshow', setupSlideshow);
    safe('observeReveal', () => observeReveal(document));
    safe('moveCatalogQuickAdds', moveCatalogQuickAdds);
  });

  // Re-run nach dynamischen Renderings (Filter, Pagination)
  document.addEventListener('shopify:section:load', () => moveCatalogQuickAdds());
  if (window.MutationObserver) {
    const mo = new MutationObserver(() => moveCatalogQuickAdds());
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
